import { useCallback, useEffect, useRef } from "react";
import { useNavigationHistory } from "../../../../hooks/useNavigationHistory";
import { GridItemAction } from "./useGridItemState";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";

interface UseGridItemHandlersProps {
  item: any;
  productType: string;
  priceMode: string;
  productPrefix: string;
  dispatch: React.Dispatch<GridItemAction>;
  setShowVideoIcon?: (value: boolean) => void;
  setImageUri?: (value: string) => void;
  setShowErrorImage?: (value: boolean) => void;
  setShowImageContainer?: (value: boolean) => void;
  checkBoxSelection: (checked: boolean, item: any, productType: string) => void;
  favoriteClickedFunction: (isFavorite: boolean, item: any) => void;
  menuOptionSelected: (key: string, item: any, productType: string) => void;
  handleItemClick?: (
    item: any,
    productType: string,
    priceMode: string,
    productPrefix: string,
    addToHistory: any
  ) => void;
}

export function useGridItemHandlers({
  item,
  productType,
  priceMode,
  productPrefix,
  dispatch,
  setShowVideoIcon,
  setImageUri,
  setShowErrorImage,
  setShowImageContainer,
  checkBoxSelection,
  favoriteClickedFunction,
  menuOptionSelected,
  handleItemClick,
}: UseGridItemHandlersProps) {
  const { addRouteToHistory } = useNavigationHistory();
  const menuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
        menuTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCheckboxChange = useCallback(
    (currentCheckedState: boolean) => {
      const newCheckedState = !currentCheckedState;
      dispatch({ type: "SET_IS_ITEM_CHECKED", payload: newCheckedState });
      checkBoxSelection(newCheckedState, item, productType);
    },
    [item, productType, checkBoxSelection, dispatch]
  );

  const handleFavoritePress = useCallback(
    (isFavorite: boolean) => {
      favoriteClickedFunction(isFavorite, item);
    },
    [favoriteClickedFunction, item]
  );

  const handleImageError = useCallback(() => {
    const hasVideo = !!item?.video_url;

    if (hasVideo) {
      setShowImageContainer?.(false);
      dispatch({ type: "SET_SHOW_IMAGE_CONTAINER", payload: false });
    } else {
      setShowErrorImage?.(true);
      setShowImageContainer?.(false);
      setImageUri?.(FALLBACK_IMAGE_ASSET);

      dispatch({
        type: "HANDLE_IMAGE_ERROR",
        payload: { hasVideo, fallbackUri: FALLBACK_IMAGE_ASSET },
      });
    }
  }, [
    item?.video_url,
    dispatch,
    setShowImageContainer,
    setShowErrorImage,
    setImageUri,
  ]);

  const handleVideoPress = useCallback(() => {
    if (item?.video_url && item?.video_url?.length > 0) {
      dispatch({ type: "SET_PLAY_VIDEO", payload: true });
      setShowVideoIcon?.(false);
      dispatch({ type: "SET_SHOW_VIDEO_ICON", payload: false });
    } else if (handleItemClick && Object?.keys?.(item)?.length > 0) {
      handleItemClick(
        item,
        productType,
        priceMode,
        productPrefix,
        addRouteToHistory
      );
    }
  }, [
    item,
    productType,
    priceMode,
    productPrefix,
    handleItemClick,
    addRouteToHistory,
    dispatch,
    setShowVideoIcon,
  ]);

  const handleNoVideoAvailable = useCallback(() => {
    if (handleItemClick && Object?.keys?.(item)?.length > 0) {
      handleItemClick(
        item,
        productType,
        priceMode,
        productPrefix,
        addRouteToHistory
      );
    }
  }, [
    item,
    productType,
    priceMode,
    productPrefix,
    handleItemClick,
    addRouteToHistory,
  ]);

  const handleVideoErrorCallback = useCallback(() => {
    dispatch({ type: "SET_HTTP_ERROR_ICON", payload: true });
  }, [dispatch]);

  const handleMenuSelection = useCallback(
    (optionName: string) => {
      dispatch({ type: "SET_THREE_DOT_MENU_OPEN", payload: false });
      if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          menuOptionSelected(optionName, item, productType);
        }
        menuTimeoutRef.current = null;
      }, 100);
    },
    [menuOptionSelected, item, productType, dispatch]
  );

  const handleItemPress = useCallback(() => {
    if (handleItemClick && Object?.keys?.(item)?.length > 0) {
      handleItemClick(
        item,
        productType,
        priceMode,
        productPrefix,
        addRouteToHistory
      );
    }
  }, [
    item,
    productType,
    priceMode,
    productPrefix,
    handleItemClick,
    addRouteToHistory,
  ]);

  const handleMenuClose = useCallback(() => {
    dispatch({ type: "SET_THREE_DOT_MENU_OPEN", payload: false });
  }, [dispatch]);

  const handleThreeDotPressIn = useCallback(() => {}, []);

  const handleThreeDotPressOut = useCallback(() => {}, []);

  const handleThreeDotPress = useCallback(() => {
    dispatch({ type: "SET_THREE_DOT_MENU_OPEN", payload: true });
  }, [dispatch]);

  const handleMouseEnter = useCallback(() => {
    dispatch({ type: "SET_HOVERED", payload: true });
  }, [dispatch]);

  const handleMouseLeave = useCallback(() => {
    dispatch({ type: "SET_HOVERED", payload: false });
  }, [dispatch]);

  const handleTooltipToggle = useCallback(
    (isOpen: boolean) => {
      dispatch({ type: "SET_TOOLTIP_OPEN", payload: isOpen });
    },
    [dispatch]
  );

  return {
    handleCheckboxChange,
    handleFavoritePress,
    handleImageError,
    handleVideoPress,
    handleNoVideoAvailable,
    handleVideoErrorCallback,
    handleMenuSelection,
    handleItemPress,
    handleMenuClose,
    handleThreeDotPressIn,
    handleThreeDotPressOut,
    handleThreeDotPress,
    handleMouseEnter,
    handleMouseLeave,
    handleTooltipToggle,
  };
}
